import { Box, IconButton, type SxProps, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Dialog, { type DialogProps } from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'

export type ConfirmationDialogProps = DialogProps & {
  open: boolean
  titleContent?: JSX.Element
  bodyContent?: React.ReactNode
  maxWidth?: string
  width?: string
  cancelButtonText?: string
  confirmButtonText?: string
  centerButtonText?: string
  hideCancelButton?: boolean
  hideConfirmButton?: boolean
  isUpdateDisabled?: boolean
  isCancelDisabled?: boolean
  cancelButtonTestId?: string
  confirmButtonTestId?: string
  buttonClassName?: string
  cancelButtonClassName?: string
  confirmButtonClassName?: string
  hideXtoClose?: boolean
  centerTitle?: boolean
  noButtons?: boolean
  position?: 'fixed' | 'absolute' | 'relative' | 'static' | 'sticky' | undefined
  containerPosition?: 'fixed' | 'absolute' | 'relative' | 'static' | 'sticky' | undefined
  bottom?: string | number
  margin?: string | number
  contentSx?: SxProps
  onClose: () => void
  onCancel?: () => void
  onConfirm: () => void
  onCenterButtonClick?: () => void
}

export const Modal = (props: ConfirmationDialogProps): JSX.Element => {
  function onDialogClose (event: any, reason: string): void {
    if (reason !== 'backdropClick') {
      props.onClose()
    }
  }

  const {
    onClose, onCancel, onConfirm, onCenterButtonClick, bodyContent, title, open, titleContent, maxWidth,
    cancelButtonText, confirmButtonText, centerButtonText, hideCancelButton, hideConfirmButton,
    isUpdateDisabled, isCancelDisabled, cancelButtonTestId, confirmButtonTestId, buttonClassName, cancelButtonClassName, confirmButtonClassName, hideXtoClose, centerTitle, noButtons, contentSx,
    containerPosition, position, bottom, margin, width,
    ...other
  } =
    props
  const content = bodyContent !== null ? bodyContent : 'Are you sure you\'d like to perform this action?'
  const confirmationTitle = props.title ?? 'Confirm'

  return (
    <Dialog
      sx={{ position: containerPosition }}
      maxWidth={maxWidth ?? 'xs'}
      PaperProps={{ sx: { position, width, bottom, margin, overflow: 'hidden', '&&': { maxWidth } } }}
      open={open}
      onClose={onDialogClose}
      {...other}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: centerTitle === true && props.hideXtoClose === true ? 'center' : 'space-between' }}
        data-testid='modal-title'
      >
        {centerTitle === true && props.hideXtoClose !== true &&
          <Box></Box> // Dummy element to center the title
        }

        {titleContent != null
          ? titleContent
          : <Typography sx={ { alignSelf: 'center', fontSize: '20px', fontFamily: 'Raleway', color: 'var(--grey-900)', fontWeight: 500 } }>
            {confirmationTitle}
          </Typography>
        }

        {(props.hideXtoClose == null || !props.hideXtoClose) &&
          <IconButton
            aria-label="close"
            onClick={onClose}
            disabled={isCancelDisabled ?? false}
            sx={{
              width: 40,
              height: 40,
              color: (theme) => theme.palette.grey[900]
            }}
          >
            <CloseIcon sx={{ width: '40px', height: '40px' }} />
          </IconButton>
        }
      </DialogTitle>

      <DialogContent data-testid='modal-content' sx={ { ...contentSx, borderBottom: '1px solid var(--border)', borderTop: '1px solid var(--border)' } }>
        <Box sx={{ mt: '.5em' }}>
          {content}
        </Box>
      </DialogContent>

      {(noButtons == null || !noButtons) &&
        <DialogActions sx={{ justifyContent: 'space-between', p: '16px 32px' }}>
          {hideCancelButton !== true &&
            <Button
              data-testid={cancelButtonTestId ?? 'cancelButton'}
              disabled={isCancelDisabled ?? false}
              className={cancelButtonClassName ?? buttonClassName ?? 'modal-button'}
              variant='outlined'
              color='inherit'
              sx={{ fontWeight: 'bold', p: '12px 24px' }}
              onClick={onCancel ?? onClose}
            >
              {cancelButtonText ?? 'Cancel'}
            </Button>
          }

          {hideCancelButton === true && <span />}

          {props.onCenterButtonClick != null &&
            <Button
              data-testid='centerButton'
              onClick={props.onCenterButtonClick}
              variant='contained'
              className={buttonClassName ?? 'modal-button'}
              sx={{ p: '12px 24px' }}
            >
              {centerButtonText ?? 'Cancel'}
            </Button>
          }
          {hideConfirmButton !== true &&
            <Button
              data-testid={confirmButtonTestId ?? 'updateButton'}
              onClick={onConfirm}
              variant='contained'
              disabled={props.isUpdateDisabled ?? false}
              className={confirmButtonClassName ?? buttonClassName ?? 'modal-confirm-button'}
              sx={{ p: '12px 24px' }}
            >
              {confirmButtonText ?? 'Update'}
            </Button>
          }
        </DialogActions>
      }
    </Dialog>
  )
}
